import type { DateOnly } from '@/types/schemas';

/**
 * Calendar dates are "YYYY-MM-DD" strings. They are never turned into a local `Date`, which
 * would shift the day in some time zones — arithmetic runs on UTC dates built from the parts.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_MS = 86_400_000;

function toUtc(date: DateOnly): Date {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function fromUtc(date: Date): DateOnly {
  return date.toISOString().slice(0, 10);
}

/** Today on this device's calendar, e.g. "2026-09-28". */
export function todayDateOnly(): DateOnly {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/** "2026-10-24" → "24 Oct 2026". */
export function formatDate(date: DateOnly): string {
  const [year, month = 1, day] = date.split('-');
  return `${day ?? ''} ${MONTHS[Number(month) - 1] ?? ''} ${year ?? ''}`;
}

/** "2026-10-24" → "Oct 2026". */
export function formatMonthYear(date: DateOnly): string {
  const [year, month = 1] = date.split('-');
  return `${MONTHS[Number(month) - 1] ?? ''} ${year ?? ''}`;
}

const timestampFormat = new Intl.DateTimeFormat('en-IN', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

/** An ISO timestamp in the viewer's time zone, e.g. "28 Sept 2026, 5:42 pm". */
export function formatTimestamp(iso: string): string {
  return timestampFormat.format(new Date(iso));
}

const timeFormat = new Intl.DateTimeFormat('en-IN', { hour: 'numeric', minute: '2-digit' });

/** Just the time of an ISO timestamp, in the viewer's time zone, e.g. "5:42 pm". */
export function formatTime(iso: string): string {
  return timeFormat.format(new Date(iso));
}

/** The calendar date of an ISO timestamp, in the viewer's time zone. */
export function timestampToDateOnly(iso: string): DateOnly {
  const date = new Date(iso);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function addDays(date: DateOnly, days: number): DateOnly {
  return fromUtc(new Date(toUtc(date).getTime() + days * DAY_MS));
}

/** Adds calendar months, clamping to the month's last day (Jan 31 + 1 month → Feb 28/29). */
export function addMonths(date: DateOnly, months: number): DateOnly {
  const start = toUtc(date);
  const target = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(start.getUTCDate(), lastDay));
  return fromUtc(target);
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** A complete "YYYY-MM-DD" — what a date field holds once it is fully filled in. */
export function isDateOnly(value: string): value is DateOnly {
  return DATE_ONLY_PATTERN.test(value);
}

/** A date range as two fields hold it: either end may be blank or still half-typed. */
export interface DateRangeInput {
  from: string;
  to: string;
}

/** A date range ready for an API query; a missing end leaves that side open. */
export interface DateRange {
  from: DateOnly | undefined;
  to: DateOnly | undefined;
}

export const EMPTY_DATE_RANGE: DateRangeInput = { from: '', to: '' };

export function hasDateRange({ from, to }: DateRangeInput): boolean {
  return from !== '' || to !== '';
}

/**
 * Turns two date fields into a query range. A blank or half-typed end is left open, and the two
 * are swapped if they arrive the wrong way round, so a filter can never put a list into an
 * error state.
 */
export function normalizeDateRange({ from, to }: DateRangeInput): DateRange {
  const start = isDateOnly(from) ? from : undefined;
  const end = isDateOnly(to) ? to : undefined;
  if (start !== undefined && end !== undefined && start > end) return { from: end, to: start };
  return { from: start, to: end };
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: DateOnly, to: DateOnly): number {
  return Math.round((toUtc(to).getTime() - toUtc(from).getTime()) / DAY_MS);
}

/** Age in full years on `on`. */
export function ageOn(birthDate: DateOnly, on: DateOnly): number {
  const [by = 0, bm = 0, bd = 0] = birthDate.split('-').map(Number);
  const [ty = 0, tm = 0, td = 0] = on.split('-').map(Number);
  return ty - by - (tm < bm || (tm === bm && td < bd) ? 1 : 0);
}

/** "Good morning" / "Good afternoon" / "Good evening" for the device's clock. */
export function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

const longDateFormat = new Intl.DateTimeFormat('en-IN', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/** "Monday, 28 September 2026". */
export function formatToday(): string {
  return longDateFormat.format(new Date());
}
